import React from "react";
import styled from "styled-components";
import { formatDay } from "./overviewDates";

const Chart = styled.div`
  position: relative; padding-left: 32px;
  > svg { width: 100%; height: 174px; display: block; overflow: visible; }
  .ticks { position: absolute; top: -6px; bottom: 22px; left: 0; display: flex; flex-direction: column; justify-content: space-between; font-size: 10px; color: #86918a; }
  .labels { display: flex; justify-content: space-between; gap: 6px; margin-top: 8px; color: #86918a; font-size: 10px; }
`;
export function TrendChart({ data, mode, label, locale }) {
  const max = Math.max(4, ...data.map(row => row.reservations));
  const top = Math.ceil(max / 4) * 4;
  const x = i => (i + .5) * 600 / data.length;
  const y = value => 160 - value / top * 160;
  const points = data.map((row, i) => `${x(i)},${y(row.reservations)}`).join(" ");
  const labels = [...new Set([0, Math.floor((data.length - 1) / 2), data.length - 1])];
  return <Chart>
    <div className="ticks" aria-hidden="true">{[top, top / 2, 0].map(value => <span key={value}>{value}</span>)}</div>
    <svg viewBox="0 0 600 164" preserveAspectRatio="none" role="img" aria-label={label}>
      {[0, 80, 160].map(value => <line key={value} x1="0" x2="600" y1={value} y2={value} stroke="#eaf0ec" vectorEffect="non-scaling-stroke" />)}
      {mode === "line" && <>
        <polygon points={`${x(0)},160 ${points} ${x(data.length - 1)},160`} fill="#ee754c" fillOpacity=".08" />
        <polyline points={points} fill="none" stroke="#ee754c" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      </>}
      {data.map((row, i) => {
        const title = `${formatDay(row.date, locale)}: ${row.reservations}`;
        return mode === "bars"
          ? <rect key={row.date} x={x(i) - 600 / data.length * .32} y={y(row.reservations)} width={600 / data.length * .64} height={160 - y(row.reservations)} rx="2" fill="#99b0ad"><title>{title}</title></rect>
          : <circle key={row.date} cx={x(i)} cy={y(row.reservations)} r={data.length > 60 ? 1.5 : 3} fill="#ee754c"><title>{title}</title></circle>;
      })}
    </svg>
    <div className="labels" aria-hidden="true">{labels.map(i => <span key={i}>{formatDay(data[i].date, locale)}</span>)}</div>
  </Chart>;
}
const STATUS_COLORS = { confirmed: "#99b0ad", pending: "#e9bd80", late: "#de9466", attended: "#6e9684", completed: "#456f5e", cancelled: "#ee754c", no_show: "#b69a9a", other: "#cad4ce" };
const DonutLayout = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 22px; min-height: 204px; flex-wrap: wrap;
  .ring { width: 148px; height: 148px; position: relative; flex: 0 0 148px; }
  .ring > svg { display: block; width: 100%; height: 100%; }
  .center { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; flex-direction: column; }
  strong { font-size: 27px; font-weight: 600; line-height: 1.2; } small { font-size: 11px; color: #89948d; }
  ul { padding: 0; margin: 0; list-style: none; flex: 1; min-width: 140px; }
  li { display: flex; align-items: center; gap: 7px; font-size: 11px; margin: 9px 0; }
  li i { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  li span { flex: 1; color: #6a7f72; } li b { font-weight: 600; }
`;
export function StatusChart({ data, t, number }) {
  const total = data.reduce((sum, row) => sum + row.reservations, 0);
  let offset = 0;
  return <DonutLayout>
    <div className="ring">
      <svg viewBox="0 0 160 160" role="img" aria-label={t("restaurantOverview.statuses")}>
        <circle cx="80" cy="80" r="62" fill="none" stroke="#f0f4f1" strokeWidth="18" />
        {data.filter(row => row.reservations > 0).map(row => {
          const size = row.reservations / total * 100, start = offset;
          offset += size;
          return <circle key={row.status} cx="80" cy="80" r="62" fill="none" pathLength="100"
            stroke={STATUS_COLORS[row.status]} strokeWidth="18" strokeDasharray={`${size} ${100 - size}`} strokeDashoffset={-start} transform="rotate(-90 80 80)">
            <title>{t(`restaurantOverview.${row.status}`)}: {row.reservations}</title>
          </circle>;
        })}
      </svg>
      <div className="center"><strong>{number(total)}</strong><small>{t("restaurantOverview.total")}</small></div>
    </div>
    <ul>{data.filter(row => row.reservations > 0).map(row => <li key={row.status}>
      <i style={{ background: STATUS_COLORS[row.status] }} /><span>{t(`restaurantOverview.${row.status}`)}</span><b>{number(row.reservations)}</b><small>{number(row.reservations / total * 100, 0)}%</small>
    </li>)}</ul>
  </DonutLayout>;
}
const WeekdayRows = styled.div`
  display: grid; gap: 13px;
  > div { display: grid; grid-template-columns: 44px minmax(0, 1fr) 62px; align-items: center; gap: 12px; font-size: 11px; }
  .track { height: 8px; background: #f1f5f2; border-radius: 4px; overflow: hidden; }
  .fill { height: 100%; background: #99b0ad; border-radius: 4px; }
  b { text-align: right; font-weight: 500; color: #718378; }
`;
export function WeekdayChart({ data, locale, number, t }) {
  const max = Math.max(1, ...data.map(row => row.average));
  return <WeekdayRows>{data.map(row => <div key={row.weekday}>
    <span>{formatDay(`2026-08-${24 + row.weekday}`, locale, { weekday: "short" })}</span>
    <div className="track" aria-hidden="true"><div className="fill" style={{ width: `${row.average / max * 100}%`, background: row.average === max ? "#ee754c" : undefined }} /></div>
    <b>{t("restaurantOverview.averageValue", { value: number(row.average, 1) })}</b>
  </div>)}</WeekdayRows>;
}
const HourColumns = styled.div`
  display: grid; grid-template-columns: repeat(24, minmax(0, 1fr)); gap: 4px; height: 198px; padding-top: 12px;
  > div { min-width: 0; display: flex; flex-direction: column; align-items: stretch; justify-content: flex-end; position: relative; padding-bottom: 22px; }
  i { display: block; background: #99b0ad; border-radius: 3px 3px 0 0; min-height: 0; }
  span { position: absolute; bottom: 0; left: 0; font-size: 10px; color: #86918a; white-space: nowrap; }
`;
export function HourChart({ data, label }) {
  const max = Math.max(1, ...data.map(row => row.reservations));
  return <HourColumns role="img" aria-label={label}>{data.map(row => <div key={row.hour} title={`${String(row.hour).padStart(2, "0")}:00 · ${row.reservations}`}>
    <i style={{ height: `${row.reservations / max * 100}%`, background: row.reservations === max ? "#ee754c" : undefined }} />
    {row.hour % 4 === 0 && <span>{String(row.hour).padStart(2, "0")}:00</span>}
  </div>)}</HourColumns>;
}
